<template>
  <div class="reports-page">
    <div v-if="loading" class="loader-container">
      <div class="loader"></div>
    </div>
    
    <div class="row">
      <div class="col-md-24">
        <card>
          <template slot="header">
            <h5 class="card-category"></h5>
            <h3 class="card-title">
              <i class="tim-icons icon-chart-bar-32"></i>
              Izveštaji i statistike
            </h3>
          </template>
          
          <!-- Filteri -->
          <div class="row">
            <div class="col-md-3">
              <h4 slot="header" class="card-title">Početak</h4>
              <base-input>
                <el-date-picker 
                  type="datetime" 
                  placeholder="Početak" 
                  value-format="timestamp"
                  format="dd.MM.yyyy. HH:mm:ss" 
                  v-model="filter.startStamp">
                </el-date-picker>
              </base-input>
            </div>
            <div class="col-md-3">
              <h4 slot="header" class="card-title">Kraj</h4>
              <base-input>
                <el-date-picker 
                  type="datetime" 
                  placeholder="Kraj" 
                  value-format="timestamp"
                  format="dd.MM.yyyy. HH:mm:ss" 
                  v-model="filter.endStamp">
                </el-date-picker>
              </base-input>
            </div>
            <div class="col-md-3">
              <h4 slot="header" class="card-title">Zona</h4>
              <base-input>
                <el-select v-model="filter.clientId" class="select-primary" placeholder="Sve zone" clearable>
                  <el-option 
                    v-for="zone in zones" 
                    class="select-primary" 
                    :label="zone.name"
                    :value="zone.id" 
                    :key="zone.id">
                  </el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-3">
              <h4 slot="header" class="card-title">Kontrolor</h4>
              <base-input>
                <el-select v-model="filter.employeeId" class="select-primary" placeholder="Svi kontrolori" clearable>
                  <el-option 
                    v-for="emp in employees" 
                    class="select-primary"
                    :style="emp.active ? 'color: green;' : 'color:red'"
                    :label="emp.active ? emp.name + ' ' + emp.lastName + ' AKTIVAN' : emp.name + ' ' + emp.lastName + ' NEAKTIVAN'"
                    :value="emp.id" 
                    :key="emp.id">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          
          <div class="row">
            <div class="col-md-3">
              <base-button class="mt-3" native-type="submit" type="primary" @click="loadData()">
                Pretraži
              </base-button>
              <base-button class="mt-3" native-type="submit" type="success" @click="exportReport()">
                Export Excel
              </base-button>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Summary kartice -->
    <div class="row">
      <div class="col-md-3">
        <card>
          <div class="stat-card tickets-card">
            <div class="stat-icon">
              <i class="tim-icons icon-credit-card"></i>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ stats.totalTickets }}</span>
              <span class="stat-label">Doplatne karte</span>
              <span class="stat-amount">{{ formatCurrency(stats.ticketsAmount) }}</span>
            </div>
            <div class="stat-trend" :class="stats.ticketsTrend >= 0 ? 'up' : 'down'">
              <i :class="stats.ticketsTrend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
              {{ Math.abs(stats.ticketsTrend) }}%
            </div>
          </div>
        </card>
      </div>

      <div class="col-md-3">
        <card>
          <div class="stat-card sms-card">
            <div class="stat-icon">
              <i class="tim-icons icon-chat-33"></i>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ stats.totalSms }}</span>
              <span class="stat-label">SMS naplate</span>
              <span class="stat-amount">{{ formatCurrency(stats.smsAmount) }}</span>
            </div>
            <div class="stat-trend" :class="stats.smsTrend >= 0 ? 'up' : 'down'">
              <i :class="stats.smsTrend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
              {{ Math.abs(stats.smsTrend) }}%
            </div>
          </div>
        </card>
      </div>

      <div class="col-md-3">
        <card>
          <div class="stat-card digital-card">
            <div class="stat-icon">
              <i class="tim-icons icon-mobile"></i>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ stats.totalDigital }}</span>
              <span class="stat-label">Digitalne karte</span>
              <span class="stat-amount">{{ formatCurrency(stats.digitalAmount) }}</span>
            </div>
            <div class="stat-trend" :class="stats.digitalTrend >= 0 ? 'up' : 'down'">
              <i :class="stats.digitalTrend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
              {{ Math.abs(stats.digitalTrend) }}%
            </div>
          </div>
        </card>
      </div>

      <div class="col-md-3">
        <card>
          <div class="stat-card scans-card">
            <div class="stat-icon">
              <i class="tim-icons icon-camera-18"></i>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ stats.totalScans }}</span>
              <span class="stat-label">Skeniranja</span>
              <span class="stat-sub">{{ stats.totalControls }} kontrola</span>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Grafici -->
    <div class="row">
      <!-- Hourly chart -->
      <div class="col-md-8">
        <card>
          <template slot="header">
            <h4 class="card-title">Aktivnost po satima</h4>
            <div class="chart-legend">
              <span class="legend-item sms"><i></i> SMS</span>
              <span class="legend-item digital"><i></i> Digitalne</span>
              <span class="legend-item tickets"><i></i> Doplatne</span>
            </div>
          </template>
          <div class="chart-container" ref="hourlyChart"></div>
        </card>
      </div>

      <!-- Distribution pie chart -->
      <div class="col-md-4">
        <card>
          <template slot="header">
            <h4 class="card-title">Distribucija prihoda</h4>
          </template>
          <div class="chart-container" ref="pieChart"></div>
          <div class="pie-legend">
            <div class="pie-item" v-for="(item, idx) in pieData" :key="idx">
              <span class="pie-color" :style="{ background: item.color }"></span>
              <span class="pie-label">{{ item.name }}</span>
              <span class="pie-value">{{ item.percent }}%</span>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Daily trend chart -->
    <div class="row">
      <div class="col-md-12">
        <card>
          <template slot="header">
            <h4 class="card-title">Trend po danima</h4>
            <el-radio-group v-model="dailyChartType" size="small">
              <el-radio-button label="count">Broj</el-radio-button>
              <el-radio-button label="amount">Iznos</el-radio-button>
            </el-radio-group>
          </template>
          <div class="chart-container-large" ref="dailyChart"></div>
        </card>
      </div>
    </div>

    <!-- Employee stats table -->
    <div class="row">
      <div class="col-md-12">
        <card>
          <template slot="header">
            <h4 class="card-title">
              <i class="tim-icons icon-single-02"></i> Statistika po zaposlenima
            </h4>
            <base-input style="width: 250px; float: right;">
              <el-input
                v-model="employeeSearch"
                placeholder="Pretraži zaposlene..."
                prefix-icon="el-icon-search"
                size="small"
                clearable>
              </el-input>
            </base-input>
          </template>
          
          <el-table
            :data="filteredEmployeeStats"
            style="width: 100%"
            :default-sort="{ prop: 'totalTickets', order: 'descending' }">

            <el-table-column min-width="180" prop="name" label="Zaposleni" sortable>
              <template slot-scope="scope">
                <div class="employee-cell">
                  <div class="employee-avatar">{{ getInitials(scope.row.name) }}</div>
                  <div class="employee-info">
                    <span class="employee-name">{{ scope.row.name }}</span>
                    <span class="employee-number">{{ scope.row.serviceNumber }}</span>
                  </div>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column min-width="100" prop="totalScans" label="Skeniranja" sortable align="center">
              <template slot-scope="scope">
                <span class="cell-badge scan">{{ scope.row.totalScans }}</span>
              </template>
            </el-table-column>
            
            <el-table-column min-width="100" prop="totalControls" label="Kontrole" sortable align="center">
              <template slot-scope="scope">
                <span class="cell-badge control">{{ scope.row.totalControls }}</span>
              </template>
            </el-table-column>
            
            <el-table-column min-width="100" prop="totalTickets" label="Doplatne" sortable align="center">
              <template slot-scope="scope">
                <span class="cell-badge ticket">{{ scope.row.totalTickets }}</span>
              </template>
            </el-table-column>
            
            <el-table-column min-width="80" prop="yellowCards" label="Žute" sortable align="center">
              <template slot-scope="scope">
                <span class="cell-badge yellow">{{ scope.row.yellowCards }}</span>
              </template>
            </el-table-column>
            
            <el-table-column min-width="80" prop="redCards" label="Crvene" sortable align="center">
              <template slot-scope="scope">
                <span class="cell-badge red">{{ scope.row.redCards }}</span>
              </template>
            </el-table-column>
            
            <el-table-column min-width="100" prop="cancellations" label="Stornirano" sortable align="center">
              <template slot-scope="scope">
                <span class="cell-badge cancel">{{ scope.row.cancellations }}</span>
              </template>
            </el-table-column>
            
            <el-table-column min-width="120" prop="ticketsAmount" label="Iznos" sortable align="right">
              <template slot-scope="scope">
                <span class="cell-amount">{{ formatCurrency(scope.row.ticketsAmount) }}</span>
              </template>
            </el-table-column>
            
            <!-- <el-table-column min-width="150" label="Efikasnost" align="center">
              <template slot-scope="scope">
                <div class="efficiency-bar">
                  <div class="efficiency-fill" :style="{ width: scope.row.efficiency + '%' }"></div>
                  <span class="efficiency-text">{{ scope.row.efficiency }}%</span>
                </div>
              </template>
            </el-table-column> -->
          </el-table>
        </card>
      </div>
    </div>

    <!-- Parking zone heatmap -->
    <div class="row">
      <div class="col-md-12">
        <card>
          <template slot="header">
            <h4 class="card-title">
              <i class="tim-icons icon-square-pin"></i> Aktivnost po zonama
            </h4>
          </template>
          <div class="zones-grid">
            <div
              v-for="zone in zoneStats"
              :key="zone.id"
              class="zone-item"
              :class="getZoneClass(zone.activity)">
              <span class="zone-name">{{ zone.name }}</span>
              <span class="zone-tickets">{{ zone.tickets }} doplatnih</span>
              <span class="zone-scans">{{ zone.scans }} skeniranja</span>
            </div>
          </div>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, DatePicker, Select, Option, RadioGroup, RadioButton, Input } from 'element-ui';
import axios from '@/util/axiosinstance';
import * as echarts from 'echarts';

export default {
  name: 'Reports',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [RadioGroup.name]: RadioGroup,
    [RadioButton.name]: RadioButton,
    [Input.name]: Input
  },
  mounted() {
    this.getZones();
    this.getEmployees();
    // this.loadData();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.hourlyChart) this.hourlyChart.dispose();
    if (this.pieChart) this.pieChart.dispose();
    if (this.dailyChart) this.dailyChart.dispose();
  },
  data() {
    const now = new Date();
    const startStamp = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime();
    const endStamp = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).getTime();

    return {
      url: 'https://budva.scancar.org:8443/kuca',
      fileUrl: 'https://budva.scancar.org:8443',
      loading: false,
      
      filter: {
        startStamp: startStamp,
        endStamp: endStamp,
        clientId: null,
        employeeId: null
      },
      
      zones: [],
      employees: [],
      employeeSearch: '',
      dailyChartType: 'count',
      
      stats: {
        totalTickets: 0,
        ticketsAmount: 0,
        ticketsTrend: 0,
        totalSms: 0,
        smsAmount: 0,
        smsTrend: 0,
        totalDigital: 0,
        digitalAmount: 0,
        digitalTrend: 0,
        totalScans: 0,
        totalControls: 0
      },
      
      hourlyData: {
        hours: [],
        sms: [],
        digital: [],
        tickets: []
      },
      
      dailyData: {
        dates: [],
        sms: [],
        digital: [],
        tickets: [],
        smsAmount: [],
        digitalAmount: [],
        ticketsAmount: []
      },
      
      pieData: [
        { name: 'SMS naplate', value: 0, percent: 0, color: '#67C23A' },
        { name: 'Digitalne karte', value: 0, percent: 0, color: '#409EFF' },
        { name: 'Doplatne karte', value: 0, percent: 0, color: '#E6A23C' }
      ],
      
      employeeStats: [],
      zoneStats: [],
      
      // Charts instances
      hourlyChart: null,
      pieChart: null,
      dailyChart: null
    };
  },
  
  computed: {
    filteredEmployeeStats() {
      if (!this.employeeSearch) return this.employeeStats;
      const search = this.employeeSearch.toLowerCase();
      return this.employeeStats.filter(e => 
        e.name.toLowerCase().includes(search) ||
        (e.serviceNumber && e.serviceNumber.toLowerCase().includes(search))
      );
    }
  },
  
  watch: {
    dailyChartType() {
      this.updateDailyChart();
    }
  },
  
  methods: {
    getZones() {
      axios.get(this.url + '/client/getClients').then(result => {
        if (result.data.result === 'ok') {
          this.zones = result.data.clients;
        }
      });
    },
    
    getEmployees() {
      axios.get(this.url + '/employee/getEmployees').then(result => {
        if (result.data.result === 'ok') {
          this.employees = result.data.employees;
        }
      });
    },
    
    async loadData() {
      if (!this.filter.startStamp || !this.filter.endStamp) return;
      
      this.loading = true;
      
      try {
        const body = {
          startDate: this.filter.startStamp,
          endDate: this.filter.endStamp,
          parkingId: this.filter.clientId || '',
          employeeId: this.filter.employeeId || ''
        };
        
        const response = await axios.post(this.url + '/reports/getReportStats', body);
        const data = response.data;
        
        if (data.result !== 'ok') {
          this.$notify({
            message: data.reason || 'Greška pri učitavanju statistike',
            timeout: 3000,
            icon: 'tim-icons icon-bell-55',
            horizontalAlign: "right",
            verticalAlign: "top",
            type: 'danger'
          });
          return;
        }
        
        // Update summary stats
        this.stats = {
          totalTickets: data.totalTickets || 0,
          ticketsAmount: data.ticketsAmount || 0,
          ticketsTrend: data.ticketsTrend || 0,
          totalSms: data.totalSms || 0,
          smsAmount: data.smsAmount || 0,
          smsTrend: data.smsTrend || 0,
          totalDigital: data.totalDigital || 0,
          digitalAmount: data.digitalAmount || 0,
          digitalTrend: data.digitalTrend || 0,
          totalScans: data.totalScans || 0,
          totalControls: data.totalControls || 0
        };
        
        // Update hourly data
        this.hourlyData = data.hourlyData || {
          hours: Array.from({ length: 24 }, (_, i) => `${i}:00`),
          sms: new Array(24).fill(0),
          digital: new Array(24).fill(0),
          tickets: new Array(24).fill(0)
        };
        
        // Update daily data
        this.dailyData = data.dailyData || {
          dates: [],
          sms: [],
          digital: [],
          tickets: [],
          smsAmount: [],
          digitalAmount: [],
          ticketsAmount: []
        };
        
        // Update pie data
        const total = this.stats.smsAmount + this.stats.digitalAmount + this.stats.ticketsAmount;
        this.pieData = [
          { 
            name: 'SMS naplate', 
            value: this.stats.smsAmount, 
            percent: total > 0 ? Math.round((this.stats.smsAmount / total) * 100) : 0,
            color: '#67C23A'
          },
          { 
            name: 'Digitalne karte', 
            value: this.stats.digitalAmount, 
            percent: total > 0 ? Math.round((this.stats.digitalAmount / total) * 100) : 0,
            color: '#409EFF'
          },
          { 
            name: 'Doplatne karte', 
            value: this.stats.ticketsAmount, 
            percent: total > 0 ? Math.round((this.stats.ticketsAmount / total) * 100) : 0,
            color: '#E6A23C'
          }
        ];
        
        // Update employee stats
        this.employeeStats = data.employeeStats || [];
        
        // Update zone stats
        this.zoneStats = data.zoneStats || [];
        
        // Update charts
        this.$nextTick(() => {
          this.initHourlyChart();
          this.initPieChart();
          this.initDailyChart();
        });
        
      } catch (error) {
        console.error('Error loading report data:', error);
        this.$notify({
          message: 'Greška pri učitavanju statistike',
          timeout: 3000,
          icon: 'tim-icons icon-bell-55',
          horizontalAlign: "right",
          verticalAlign: "top",
          type: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },
    
    initHourlyChart() {
      if (!this.$refs.hourlyChart) return;
      
      if (this.hourlyChart) {
        this.hourlyChart.dispose();
      }
      
      this.hourlyChart = echarts.init(this.$refs.hourlyChart);
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(30, 30, 47, 0.95)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          textStyle: { color: '#fff' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.hourlyData.hours,
          axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.2)' } },
          axisLabel: { color: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.1)' } },
          axisLabel: { color: 'rgba(255, 255, 255, 0.7)' }
        },
        series: [
          {
            name: 'SMS',
            type: 'bar',
            stack: 'total',
            data: this.hourlyData.sms,
            itemStyle: { color: '#67C23A', borderRadius: [0, 0, 0, 0] }
          },
          {
            name: 'Digitalne',
            type: 'bar',
            stack: 'total',
            data: this.hourlyData.digital,
            itemStyle: { color: '#409EFF' }
          },
          {
            name: 'Doplatne',
            type: 'bar',
            stack: 'total',
            data: this.hourlyData.tickets,
            itemStyle: { color: '#E6A23C', borderRadius: [4, 4, 0, 0] }
          }
        ]
      };
      
      this.hourlyChart.setOption(option);
    },
    
    initPieChart() {
      if (!this.$refs.pieChart) return;
      
      if (this.pieChart) {
        this.pieChart.dispose();
      }
      
      this.pieChart = echarts.init(this.$refs.pieChart);
      
      const option = {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(30, 30, 47, 0.95)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          textStyle: { color: '#fff' },
          formatter: '{b}: {c} EUR ({d}%)'
        },
        series: [
          {
            type: 'pie',
            radius: ['50%', '75%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            label: { show: false },
            emphasis: {
              label: { show: false }
            },
            labelLine: { show: false },
            data: this.pieData.map(item => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.color }
            }))
          }
        ]
      };
      
      this.pieChart.setOption(option);
    },
    
    initDailyChart() {
      if (!this.$refs.dailyChart) return;
      
      if (this.dailyChart) {
        this.dailyChart.dispose();
      }
      
      this.dailyChart = echarts.init(this.$refs.dailyChart);
      this.updateDailyChart();
    },
    
    updateDailyChart() {
      if (!this.dailyChart) return;
      
      const isAmount = this.dailyChartType === 'amount';
      
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(30, 30, 47, 0.95)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          textStyle: { color: '#fff' }
        },
        legend: {
          data: ['SMS', 'Digitalne', 'Doplatne'],
          textStyle: { color: 'rgba(255, 255, 255, 0.7)' },
          top: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '40px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.dailyData.dates,
          axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.2)' } },
          axisLabel: { color: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.1)' } },
          axisLabel: { 
            color: 'rgba(255, 255, 255, 0.7)',
            formatter: isAmount ? '{value} EUR' : '{value}'
          }
        },
        series: [
          {
            name: 'SMS',
            type: 'line',
            smooth: true,
            data: isAmount ? this.dailyData.smsAmount : this.dailyData.sms,
            lineStyle: { color: '#67C23A', width: 2 },
            itemStyle: { color: '#67C23A' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(103, 194, 58, 0.3)' },
                { offset: 1, color: 'rgba(103, 194, 58, 0.05)' }
              ])
            }
          },
          {
            name: 'Digitalne',
            type: 'line',
            smooth: true,
            data: isAmount ? this.dailyData.digitalAmount : this.dailyData.digital,
            lineStyle: { color: '#409EFF', width: 2 },
            itemStyle: { color: '#409EFF' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
              ])
            }
          },
          {
            name: 'Doplatne',
            type: 'line',
            smooth: true,
            data: isAmount ? this.dailyData.ticketsAmount : this.dailyData.tickets,
            lineStyle: { color: '#E6A23C', width: 2 },
            itemStyle: { color: '#E6A23C' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(230, 162, 60, 0.3)' },
                { offset: 1, color: 'rgba(230, 162, 60, 0.05)' }
              ])
            }
          }
        ]
      };
      
      this.dailyChart.setOption(option);
    },
    
    handleResize() {
      if (this.hourlyChart) this.hourlyChart.resize();
      if (this.pieChart) this.pieChart.resize();
      if (this.dailyChart) this.dailyChart.resize();
    },
    
    formatCurrency(value) {
      if (!value) return '0 EUR';
      return new Intl.NumberFormat('sr-RS').format(value) + ' EUR';
    },
    
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },
    
    tableRowClassName({ row, rowIndex }) {
      if (row.efficiency >= 80) return 'high-efficiency';
      if (row.efficiency >= 50) return 'medium-efficiency';
      return '';
    },
    
    getZoneClass(activity) {
      if (activity >= 80) return 'zone-hot';
      if (activity >= 50) return 'zone-medium';
      if (activity >= 20) return 'zone-low';
      return 'zone-cold';
    },
    
    async exportReport() {
      try {
        this.loading = true;
        const body = {
          startDate: this.filter.startStamp,
          endDate: this.filter.endStamp,
          parkingId: this.filter.clientId || '',
          employeeId: this.filter.employeeId || ''
        };
        
        const response = await axios.post(this.url + '/reports/exportReportStats', body);
        
        if (response.data.result === 'ok') {
          const link = document.createElement('a');
          link.href = this.fileUrl + "/opomene/" + response.data.fileName;
          link.download = "";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          this.$notify({
            message: 'Izveštaj uspešno exportovan',
            timeout: 3000,
            icon: 'tim-icons icon-bell-55',
            horizontalAlign: "right",
            verticalAlign: "top",
            type: 'success'
          });
        } else {
          this.$notify({
            message: response.data.reason || 'Greška pri exportovanju',
            timeout: 3000,
            icon: 'tim-icons icon-bell-55',
            horizontalAlign: "right",
            verticalAlign: "top",
            type: 'danger'
          });
        }
      } catch (error) {
        console.error('Error exporting report:', error);
        this.$notify({
          message: 'Greška pri exportovanju izveštaja',
          timeout: 3000,
          icon: 'tim-icons icon-bell-55',
          horizontalAlign: "right",
          verticalAlign: "top",
          type: 'danger'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.reports-page {
  padding: 20px;
  background: #1e1e2f;
  min-height: 100vh;
}

/* Header */
.reports-header {
  background: linear-gradient(135deg, #2d2d44 0%, #1e1e2f 100%);
  border-radius: 12px;
  padding: 20px 25px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.header-title i {
  font-size: 28px;
  color: #409EFF;
}

.header-title h2 {
  margin: 0;
  color: #fff;
  font-size: 22px;
  font-weight: 600;
}

.filters-row {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: linear-gradient(135deg, #2d2d44 0%, #252538 100%);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
}

.tickets-card::before { background: #E6A23C; }
.sms-card::before { background: #67C23A; }
.digital-card::before { background: #409EFF; }
.scans-card::before { background: #F56C6C; }

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tickets-card .stat-icon { background: rgba(230, 162, 60, 0.2); }
.sms-card .stat-icon { background: rgba(103, 194, 58, 0.2); }
.digital-card .stat-icon { background: rgba(64, 158, 255, 0.2); }
.scans-card .stat-icon { background: rgba(245, 108, 108, 0.2); }

.stat-icon i {
  font-size: 24px;
}

.tickets-card .stat-icon i { color: #E6A23C; }
.sms-card .stat-icon i { color: #67C23A; }
.digital-card .stat-icon i { color: #409EFF; }
.scans-card .stat-icon i { color: #F56C6C; }

.stat-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.stat-amount {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  margin-top: 4px;
}

.stat-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
}

.stat-trend {
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  border-radius: 20px;
}

.stat-trend.up {
  color: #67C23A;
  background: rgba(103, 194, 58, 0.15);
}

.stat-trend.down {
  color: #F56C6C;
  background: rgba(245, 108, 108, 0.15);
}

/* Charts */
.charts-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

@media (max-width: 1200px) {
  .charts-row {
    grid-template-columns: 1fr;
  }
}

.chart-card {
  background: linear-gradient(135deg, #2d2d44 0%, #252538 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.chart-header h3 {
  margin: 0;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.chart-header h3 i {
  color: #409EFF;
}

.chart-legend {
  display: flex;
  gap: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.legend-item i {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.legend-item.sms i { background: #67C23A; }
.legend-item.digital i { background: #409EFF; }
.legend-item.tickets i { background: #E6A23C; }

.chart-container {
  height: 280px;
}

.hourly-chart .chart-container {
  height: 300px;
}

.daily-chart {
  margin-bottom: 20px;
}

.daily-chart .chart-container {
  height: 320px;
}

/* Pie chart specifics */
.pie-chart .chart-container {
  height: 200px;
}

.pie-legend {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pie-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pie-color {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  flex-shrink: 0;
}

.pie-label {
  flex: 1;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.pie-value {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

/* Employee Table */
.table-card {
  background: linear-gradient(135deg, #2d2d44 0%, #252538 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.table-header h3 {
  margin: 0;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-header h3 i {
  color: #409EFF;
}

.employee-search {
  width: 250px;
}

.employee-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.employee-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #409EFF, #67C23A);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
}

.employee-info {
  display: flex;
  flex-direction: column;
}

.employee-name {
  font-weight: 500;
  color: #fff;
}

.employee-number {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

.cell-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.cell-badge.scan { background: rgba(245, 108, 108, 0.15); color: #F56C6C; }
.cell-badge.control { background: rgba(144, 147, 153, 0.15); color: #909399; }
.cell-badge.ticket { background: rgba(230, 162, 60, 0.15); color: #E6A23C; }
.cell-badge.yellow { background: rgba(255, 230, 0, 0.15); color: #FFE600; }
.cell-badge.red { background: rgba(245, 108, 108, 0.15); color: #F56C6C; }
.cell-badge.cancel { background: rgba(144, 147, 153, 0.15); color: #909399; }

.cell-amount {
  font-weight: 600;
  color: #67C23A;
}

.efficiency-bar {
  width: 100%;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.efficiency-fill {
  height: 100%;
  background: linear-gradient(90deg, #409EFF, #67C23A);
  border-radius: 12px;
  transition: width 0.5s ease;
}

.efficiency-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Zones heatmap */
.heatmap-card {
  margin-bottom: 20px;
}

.zones-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.zone-item {
  padding: 15px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: transform 0.2s ease;
}

.zone-item:hover {
  transform: scale(1.02);
}

.zone-hot {
  background: linear-gradient(135deg, rgba(245, 108, 108, 0.3), rgba(245, 108, 108, 0.1));
  border: 1px solid rgba(245, 108, 108, 0.4);
}

.zone-medium {
  background: linear-gradient(135deg, rgba(230, 162, 60, 0.3), rgba(230, 162, 60, 0.1));
  border: 1px solid rgba(230, 162, 60, 0.4);
}

.zone-low {
  background: linear-gradient(135deg, rgba(103, 194, 58, 0.3), rgba(103, 194, 58, 0.1));
  border: 1px solid rgba(103, 194, 58, 0.4);
}

.zone-cold {
  background: linear-gradient(135deg, rgba(144, 147, 153, 0.2), rgba(144, 147, 153, 0.05));
  border: 1px solid rgba(144, 147, 153, 0.3);
}

.zone-name {
  font-weight: 600;
  color: #fff;
  font-size: 14px;
}

.zone-tickets {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.zone-scans {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

/* Element UI overrides */
>>> .el-table {
  background: transparent !important;
}

>>> .el-table tr {
  background: transparent !important;
}

>>> .el-table th.el-table__cell {
  background: rgba(255, 255, 255, 0.05) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  font-weight: 600;
}

>>> .el-table td.el-table__cell {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

>>> .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background: rgba(64, 158, 255, 0.1) !important;
}

>>> .el-table .high-efficiency td {
  background: rgba(103, 194, 58, 0.05) !important;
}

>>> .el-table .medium-efficiency td {
  background: rgba(230, 162, 60, 0.05) !important;
}

>>> .el-input__inner {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}

>>> .el-input__inner::placeholder {
  color: rgba(255, 255, 255, 0.4) !important;
}

>>> .el-select .el-input__inner {
  background: rgba(255, 255, 255, 0.1) !important;
}

>>> .el-date-editor .el-range-input {
  background: transparent !important;
  color: #fff !important;
}

>>> .el-date-editor .el-range-separator {
  color: rgba(255, 255, 255, 0.5) !important;
}

>>> .el-radio-button__inner {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

>>> .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #409EFF !important;
  border-color: #409EFF !important;
  color: #fff !important;
}

/* Loader */
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.7);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9999;
}

.loader {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Chart containers */
.chart-container {
  height: 280px;
}

.chart-container-large {
  height: 350px;
}
</style>